import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface jpegImage {
  low?: string;
  medium?: string;
  high?: string;
}

interface ToolState {
  files: FileList | null;
  isUploading: boolean;
  uploadResponse: any;
  uploadError: any;
  isProcessing: boolean;
  processResponse: any;
  processError: any;
  downloadUrl: string | null;
  documentImageUrl: string | null;
  showIndicator: boolean;
  resultImages: jpegImage[] | null;
  fileName: string;
  fileSize: string;
  pageCount: number;
  jpegImages: {
    url: string;
    name: string;
  }[];
  exportAsFiles: FileList | null;
  pendingConversion?: {
    operationType: string;
    exportType: string;
  } | null;
  organizeExportCompleted: boolean;
}

const initialState: ToolState = {
  files: null,
  isUploading: false,
  uploadResponse: null,
  uploadError: null,
  isProcessing: false,
  processResponse: null,
  processError: null,
  downloadUrl: null,
  documentImageUrl: null,
  showIndicator: false,
  resultImages: null,
  fileName: '',
  fileSize: '',
  pageCount: 0,
  jpegImages: [],
  exportAsFiles: null,
  pendingConversion: null,
  organizeExportCompleted: false,
};

const toolSlice = createSlice({
  name: 'tool',
  initialState,
  reducers: {
    startUpload: (
      state,
      action: PayloadAction<{ fileName: string; fileSize: string }>
    ) => {
      state.showIndicator = true;
      state.isUploading = true;
      state.fileName = action.payload.fileName;
      state.fileSize = action.payload.fileSize;
    },
    setFileName: (state, action: PayloadAction<string>) => {
      state.fileName = action.payload;
    },
    uploadSuccess: (state, action) => {
      state.isUploading = false;
      state.uploadResponse = action.payload;
    },
    uploadFailure: (state, action) => {
      state.isUploading = false;
      state.uploadError = action.payload;
    },
    startProcess: (state) => {
      state.isProcessing = true;
    },
    processSuccess: (state, action) => {
      state.isProcessing = false;
      state.processResponse = action.payload;
    },
    processFailure: (state, action) => {
      state.isProcessing = false;
      state.processError = action.payload;
    },
    updateDownloadUrl: (state, action: PayloadAction<string>) => {
      state.downloadUrl = action.payload;
      // state.showIndicator = false;
    },
    updateDocumentImageUrl: (state, action: PayloadAction<string>) => {
      state.documentImageUrl = action.payload;
    },
    updateIndicator: (state, action: PayloadAction<boolean>) => {
      state.showIndicator = action.payload;
    },
    updateImages: (state, action: PayloadAction<jpegImage[]>) => {
      state.resultImages = action.payload;
    },
    updateJpegImages: (
      state,
      action: PayloadAction<{ url: string; name: string }[]>
    ) => {
      state.jpegImages = action.payload;
    },
    addJpegImage: (
      state,
      action: PayloadAction<{ url: string; name: string; key: string }>
    ) => {
      state.jpegImages.push(action.payload);
    },
    setPageCount: (state, action: PayloadAction<number>) => {
      state.pageCount = action.payload;
    },
    setFiles: (state, action: PayloadAction<FileList>) => {
      state.files = action.payload;
    },
    reset: (state) => {
      state.isUploading = false;
      state.uploadResponse = null;
      state.uploadError = null;
      state.isProcessing = false;
      state.processResponse = null;
      state.processError = null;
      state.downloadUrl = null;
      state.documentImageUrl = null;
      state.showIndicator = false;
      state.resultImages = null;
      state.fileName = '';
      state.fileSize = '';
      state.pageCount = 0;
      state.jpegImages = [];
      state.files = null;
    },
    setExportAsFiles: (state, action: PayloadAction<FileList | null>) => {
      state.exportAsFiles = action.payload;
    },
    resetAll: (state) => {
      state.isUploading = false;
      state.uploadResponse = null;
      state.uploadError = null;
      state.isProcessing = false;
      state.processResponse = null;
      state.processError = null;
      state.downloadUrl = null;
      state.documentImageUrl = null;
      state.showIndicator = false;
      state.resultImages = null;
      state.fileName = '';
      state.fileSize = '';
      state.pageCount = 0;
      state.jpegImages = [];
      state.files = null;
      state.exportAsFiles = null;
    },
    setPendingConversion: (
      state,
      action: PayloadAction<{
        operationType: string;
        exportType: string;
      } | null>
    ) => {
      state.pendingConversion = action.payload;
    },
    setOrganizeExportCompleted(state) {
      state.organizeExportCompleted = true;
    },
    resetOrganizeExportCompleted(state) {
      state.organizeExportCompleted = false;
    },
  },
});

export const {
  startUpload,
  uploadSuccess,
  uploadFailure,
  startProcess,
  processSuccess,
  processFailure,
  updateDownloadUrl,
  updateDocumentImageUrl,
  updateIndicator,
  updateImages,
  updateJpegImages,
  addJpegImage,
  reset,
  setFileName,
  setPageCount,
  setFiles,
  setExportAsFiles,
  resetAll,
  setPendingConversion,
  setOrganizeExportCompleted,
  resetOrganizeExportCompleted,
} = toolSlice.actions;

export default toolSlice.reducer;
